import { useState, type CSSProperties } from 'react'

import { type Face, faceForName, foodDeck, iconUrl } from '~/data/food-emoji'
import type { Recipe } from '~/data/nutrition'

/**
 * A face drawn at the size of the text around it: its icon where it has one, its emoji where
 * not. The icon stands a little larger than the letters, as an emoji's picture does.
 */
export function FaceGlyph({ face }: { face: Face }) {
  // An icon that would not load — offline before it was ever fetched — gives way to the emoji.
  const [failed, setFailed] = useState<string | null>(null)
  const url = face.icon === null ? undefined : iconUrl(face.icon)
  if (url === undefined || failed === url) return face.emoji
  return (
    <img
      src={url}
      alt=""
      draggable={false}
      data-testid={`food-icon:${face.icon}`}
      className="size-[1.3em] object-contain"
      onError={() => {
        setFailed(url)
      }}
    />
  )
}

/** One face on its round ground — the whole of a mark, or one card of a deck. */
const GROUND = 'grid place-items-center rounded-full bg-surface leading-none ring-1 ring-white/10'

/**
 * Where each face of a deck stands inside the mark's square, as fractions of its side, the one
 * in front first: in front and largest at the bottom of the near side, the others toward the far
 * side — at its top, and for a third at its bottom. They only just overlap, so every face shows
 * whole rather than any of them hiding behind the one in front.
 */
const CLUSTERS: Readonly<Record<number, readonly { x: number; y: number; side: number }[]>> = {
  2: [
    { x: 0, y: 0.36, side: 0.64 },
    { x: 0.44, y: 0, side: 0.56 },
  ],
  3: [
    { x: 0, y: 0.38, side: 0.62 },
    { x: 0.46, y: 0.02, side: 0.5 },
    { x: 0.54, y: 0.54, side: 0.46 },
  ],
}

/**
 * The faces a food, a recipe or a meal wears, drawn in the square a single mark takes, so a
 * list of them reads as one column whatever each wears. One face is the plain mark; more are a
 * cluster around the one in front, each ringed in the dark so they part.
 */
export function FaceDeck({ deck, size }: { deck: readonly Face[]; size: string }) {
  const cluster = CLUSTERS[deck.length]
  if (cluster === undefined) {
    return (
      <span aria-hidden className={`${GROUND} shrink-0 ${size}`}>
        {deck[0] === undefined ? null : <FaceGlyph face={deck[0]} />}
      </span>
    )
  }
  return (
    <span aria-hidden data-testid="face-deck" className={`relative shrink-0 ${size}`}>
      {deck
        .map((face, at) => ({ face, at, card: cluster[at] }))
        .reverse()
        .map(({ face, at, card }) => {
          if (card === undefined) return null
          const style: CSSProperties = {
            insetInlineStart: `${String(card.x * 100)}%`,
            top: `${String(card.y * 100)}%`,
            width: `${String(card.side * 100)}%`,
            height: `${String(card.side * 100)}%`,
            fontSize: `${String(card.side * 1.25)}em`,
          }
          return (
            <span
              key={at}
              style={style}
              className={`${GROUND} absolute shadow-[0_0_0_1.5px_rgb(0_0_0/0.6)]`}
            >
              <FaceGlyph face={face} />
            </span>
          )
        })}
    </span>
  )
}

/**
 * The face a food wears wherever it is named, and the deck a recipe or a meal wears — see the
 * nutrition spec, Face and Deck. It is hidden from anything reading the page: the name is
 * right beside it, and a row that read out "bowl of food, Porridge" would be saying the same
 * thing twice in two vocabularies.
 *
 * A ground behind it rather than a bare glyph, so a list of them reads as a column of marks at
 * one size instead of a ragged edge — emoji are drawn at whatever width each font feels like.
 */
export function FoodMark({
  food = null,
  name = '',
  deck,
  size = 'size-9 text-lg',
}: {
  /** The food it stands for, where there is one: a recipe wears its deck. */
  food?: { readonly name: string; readonly recipe?: Recipe | null } | null
  /** What it is called, where there is no food — a quick add's own line, an AI's answer. */
  name?: string
  /** The faces to wear outright — a meal's deck. */
  deck?: readonly Face[]
  size?: string
}) {
  const faces = deck ?? (food === null ? [faceForName(name)] : foodDeck(food))
  return <FaceDeck deck={faces} size={size} />
}
